import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { approveFundedRoadmap, draftFundedRoadmap, askProgram, checkProgramWave, createApplication, createAppProject, dispatchProgramAlerts, getProgram, getPrograms, ingestProgramDependencies, signProgramCharter, updateProgramFunding } from "../lib/api";
import CreateAppModal from "../components/CreateAppModal";
import ProgramAnalytics from "../components/ProgramAnalytics";
import ProgramListDirectory from "../components/ProgramListDirectory";
import StorySpine from "../components/StorySpine";
import SubFlowNav, { FocusPanel, SubFlowPager } from "../components/SubFlow";
import { Badge } from "../lib/ui";
import { canApproveFundedRoadmap, canCreateApp, canCreateProgram, canSetProgramFunding, canSignProgramCharter, canWriteProgram, showStorySpine } from "../lib/master";
import { withApp } from "../lib/story";
import {
  PROGRAM_EXECUTION_DRILL,
  PROGRAM_EXECUTION_PANE_DETAIL,
  PROGRAM_EXECUTION_PANES,
  PROGRAM_FLOWS,
  PROGRAM_FOCUS,
  PROGRAM_GOVERN_DRILL,
  PROGRAM_GOVERN_PANE_DETAIL,
  PROGRAM_GOVERN_PANES,
  PROGRAM_STEPS,
  programAnalyticsHref,
  programListHref,
  programPaneHref,
  programStepHref,
  programView,
  resolveProgramModule,
  resolveProgramPane,
} from "../lib/programFlows";

function tone(status) {
  if (status === "red" || status === "Complete") return status === "red" ? "rose" : "green";
  if (status === "amber" || status === "In review" || status === "Blocked / pending") return "amber";
  if (status === "green" || status === "In progress") return status === "green" ? "green" : "cyan";
  return "slate";
}

export default function Program({ user }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const programId = params.get("program");
  const rawModule = params.get("module");
  const module = resolveProgramModule(rawModule);
  const pane = resolveProgramPane(module, rawModule, params.get("pane"));
  const view = programView(params);
  const hasPaneNav = view === "step" && (module === "govern" || module === "execution");
  const paneFlows = module === "govern" ? PROGRAM_GOVERN_PANES : module === "execution" ? PROGRAM_EXECUTION_PANES : [];
  const paneDrill = module === "govern" ? PROGRAM_GOVERN_DRILL : module === "execution" ? PROGRAM_EXECUTION_DRILL : [];
  const paneDetail = module === "govern" ? PROGRAM_GOVERN_PANE_DETAIL : PROGRAM_EXECUTION_PANE_DETAIL;
  const selectedProgram = programId;
  const meta = PROGRAM_FOCUS[module] || PROGRAM_FOCUS.overview;
  const [data, setData] = useState(null);
  const [loadingProgram, setLoadingProgram] = useState(false);
  const [error, setError] = useState("");
  const [ask, setAsk] = useState(null);
  const [asking, setAsking] = useState(false);

  const [check, setCheck] = useState(null);
  const [ingestMsg, setIngestMsg] = useState("");
  const [dispatchMsg, setDispatchMsg] = useState("");
  const [projectMsg, setProjectMsg] = useState("");
  const [csv, setCsv] = useState("dependency_id,source_id,source_name,target_id,target_name,dependency_type,blocking,target_wave,source_wave,owner\nDEP-109,PAY,Payment API,SAP,SAP ERP,External/Vendor,yes,WAVE-03,WAVE-01,SAP CoE");
  const mayWrite = canWriteProgram(user?.guide);
  const mayCreateProgram = canCreateProgram(user?.guide);
  const mayCreateApp = canCreateApp(user?.guide);
  const maySignCharter = canSignProgramCharter(user?.guide);
  const maySetFunding = canSetProgramFunding(user?.guide);
  const mayApproveRoadmap = canApproveFundedRoadmap(user?.guide);
  const [createAppOpen, setCreateAppOpen] = useState(false);
  const [createAppMsg, setCreateAppMsg] = useState("");
  const [charterMsg, setCharterMsg] = useState("");
  const [fundingMsg, setFundingMsg] = useState("");
  const [fundingState, setFundingState] = useState("UNFUNDED");
  const [fundingRationale, setFundingRationale] = useState("");
  const [roadmapMsg, setRoadmapMsg] = useState("");
  const [programs, setPrograms] = useState([]);
  const [listLoading, setListLoading] = useState(true);

  const refreshPrograms = () => {
    setListLoading(true);
    return getPrograms()
      .then(({ data: payload }) => setPrograms(payload.programs || []))
      .catch(() => setPrograms([]))
      .finally(() => setListLoading(false));
  };

  useEffect(() => {
    refreshPrograms();
  }, []);

  useEffect(() => {
    if (!programId) {
      setData(null);
      setError("");
      return undefined;
    }
    setLoadingProgram(true);
    setError("");
    getProgram(programId)
      .then(({ data: payload }) => setData(payload))
      .catch((err) => {
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        if (status === 404 || status === 502 || status === 504 || !err.response) {
          setError(status === 404 ? (detail || "Program not found") : "Program API is not reachable. Start the backend: uvicorn server:app --reload --host 127.0.0.1 --port 8001");
          return;
        }
        setError(typeof detail === "string" ? detail : "Could not load program governance");
      })
      .finally(() => setLoadingProgram(false));
    return undefined;
  }, [programId]);

  const coreRecord = data?.core?.record;

  useEffect(() => {
    if (coreRecord?.funding_state) setFundingState(coreRecord.funding_state);
    setFundingRationale(coreRecord?.funding_rationale || "");
  }, [coreRecord]);

  const reloadProgram = async () => {
    if (!programId) return;
    const { data: payload } = await getProgram(programId);
    setData(payload);
  };

  const runAsk = async (question) => {
    setAsking(true);
    try {
      const { data: payload } = await askProgram(question);
      setAsk(payload);
    } catch (err) {
      setAsk({ answer: err.response?.data?.detail || "Ask failed", citations: [] });
    } finally {
      setAsking(false);
    }
  };

  const runCheck = async () => {
    try {
      const { data: payload } = await checkProgramWave("PAY", "WAVE-01");
      setCheck(payload);
      if (payload.alerts_sent?.length) await reloadProgram();
    } catch (err) {
      setCheck({ ok: false, detail: err.response?.data?.detail || "Check failed", conflicts: [], alerts_sent: [] });
    }
  };

  const runDispatch = async () => {
    try {
      const { data: payload } = await dispatchProgramAlerts();
      setDispatchMsg(payload.detail || "Emailed function owners");
      await reloadProgram();
    } catch (err) {
      setDispatchMsg(err.response?.data?.detail || "Dispatch failed");
    }
  };

  const runCreateProject = async (appId) => {
    try {
      const { data: payload } = await createAppProject(appId);
      setProjectMsg(`Created ${payload.project?.id} for ${appId}`);
      await reloadProgram();
    } catch (err) {
      setProjectMsg(err.response?.data?.detail || "Create project failed");
    }
  };

  const runIngest = async () => {
    try {
      const { data: payload } = await ingestProgramDependencies(csv, "csv");
      setIngestMsg(`Upserted ${(payload.upserted || []).join(", ") || "rows"}`);
      await reloadProgram();
    } catch (err) {
      setIngestMsg(err.response?.data?.detail || "Ingest failed");
    }
  };

  const programMeta = programs.find((p) => p.id === programId);
  const stepHref = (id) => programStepHref(programId, id === "overview" ? undefined : id);
  const paneHref = (paneId) => programPaneHref(programId, module, paneId);

  if (view === "list" && listLoading) {
    return <div className="loading-screen" data-testid="page-wait">Loading programs…</div>;
  }

  if (view !== "list" && loadingProgram && !data) {
    return <div className="loading-screen" data-testid="page-wait">Loading program analytics…</div>;
  }

  if (view !== "list" && error) {
    return (
      <div className="inner-page subflow-page" data-testid="program-page">
        <Link to={programListHref()} className="analytics-back" data-testid="program-back">
          <ChevronLeft size={16} /> Back to all programs
        </Link>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  const planning = data?.planning;
  const inputs = data?.inputs;
  const dependencies = data?.dependencies;
  const waves = data?.waves;
  const raid = data?.raid;
  const dashboard = data?.dashboard;
  const approvals = data?.approvals;
  const readiness = data?.readiness;
  const hypercare = data?.hypercare;
  const copilot = data?.copilot;
  const alerts = data?.alerts;
  const core = data?.core || {};
  const programApps = core.applications || [];
  const programRecord = core.record || programMeta || {};
  const charterSigned = programRecord?.charter?.signature?.state === "SIGNED";
  const fundedRoadmap = programRecord?.funded_roadmap || {};
  const conflicts = dependencies?.conflicts || waves?.conflicts || [];
  const programCount = programs.length;

  return (
    <div className="inner-page subflow-page" data-testid="program-page">
      {view === "analytics" && (
        <Link to={programListHref()} className="analytics-back" data-testid="program-back">
          <ChevronLeft size={16} /> Back to all programs
        </Link>
      )}
      {view === "step" && (
        <Link to={programAnalyticsHref(programId)} className="analytics-back" data-testid="program-back-analytics">
          <ChevronLeft size={16} /> Back to program analytics
        </Link>
      )}

      <FocusPanel
        kicker={view === "list" ? "PROGRAM · TENANT" : `PROGRAM · ${core?.record?.id || programId || "NRG-01"} · MODEL=NONE`}
        title={
          view === "list"
            ? `${programCount} program${programCount === 1 ? "" : "s"} in scope`
            : view === "analytics"
              ? (core?.record?.name || programMeta?.name || "Programme analytics")
              : (meta.title || core?.record?.name)
        }
        detail={
          view === "list"
            ? "Select a program to see modernization analytics, then drill into each step."
            : view === "analytics"
              ? PROGRAM_FOCUS.overview.detail
              : (core?.rule || meta.detail)
        }
        badge={
          view === "list" ? (
            <Badge tone="cyan">{programCount} total</Badge>
          ) : (
            <Badge tone={core?.health?.rag === "red" ? "rose" : core?.health?.rag === "amber" ? "amber" : "green"}>
              {core?.health?.rag?.toUpperCase() || "RAG"}
            </Badge>
          )
        }
        testId="program-head"
      />

      {view === "step" && (
        <>
          <SubFlowNav items={PROGRAM_FLOWS} focus={module} testId="program-subflow" buildHref={stepHref} />
          {hasPaneNav && (
            <>
              {pane !== "overview" && (
                <Link to={paneHref("overview")} className="analytics-back" data-testid="program-pane-back">
                  <ChevronLeft size={16} /> Back to {module} overview
                </Link>
              )}
              <SubFlowNav items={paneFlows} focus={pane} testId="program-pane-subflow" param="pane" buildHref={paneHref} />
              <SubFlowPager items={paneFlows} focus={pane} testId="program-pane-pager" param="pane" buildHref={paneHref} />
            </>
          )}
          <SubFlowPager items={PROGRAM_FLOWS} focus={module} testId="program-pager" buildHref={stepHref} />
          {showStorySpine(user?.guide) && <StorySpine current="program" compact />}
        </>
      )}

      {hasPaneNav && pane === "overview" && (
        <section className="feature-panel analytics-panel" data-testid="program-pane-drill">
          <p className="kicker">DRILL DOWN · ONE TOPIC PER SCREEN</p>
          <div className="lifecycle-phase-links">
            {paneDrill.map(({ id, label }) => (
              <Link key={id} to={paneHref(id)} className="lifecycle-phase-link" data-testid={`program-pane-drill-${id}`}>
                <strong>{label}</strong>
                <span>{paneDetail[id]}</span>
                <ChevronRight size={16} />
              </Link>
            ))}
          </div>
        </section>
      )}

      {view !== "list" && !!conflicts.length && (
        <div className="error-message" data-testid="wave-conflict-banner">
          <strong>{conflicts[0].title}</strong>
          <p>{conflicts[0].message}</p>
          <p>{conflicts[0].recommendation}</p>
        </div>
      )}

      {view === "list" && (
        <ProgramListDirectory
          programs={programs}
          canCreate={mayCreateProgram}
          onRefresh={refreshPrograms}
          onViewProgram={(id) => navigate(programAnalyticsHref(id))}
        />
      )}

      {view === "analytics" && data && (
        <>
          <ProgramAnalytics programId={programId} data={data} programMeta={programMeta} />
          <section className="feature-panel analytics-panel" data-testid="program-applications">
            <div className="panel-header compact">
              <div>
                <p className="kicker">APPLICATIONS IN THIS PROGRAM</p>
                <h2>{programApps.length} decision unit{programApps.length === 1 ? "" : "s"}</h2>
              </div>
              {mayCreateApp && (
                <button
                  type="button"
                  className="primary-button"
                  data-testid="program-add-application"
                  onClick={() => {
                    setCreateAppMsg("");
                    setCreateAppOpen(true);
                  }}
                >
                  <Plus size={16} /> Add application
                </button>
              )}
            </div>
            {createAppMsg && <p className="admin-note" data-testid="program-create-app-msg">{createAppMsg}</p>}
            {!mayCreateApp && (
              <p className="muted small-note">Your role is read-only or cannot create applications. Use Product Owner, Program Manager, or App Owner.</p>
            )}
            {programApps.length === 0 ? (
              <p className="muted">No applications in this program yet. Add one to start intake.</p>
            ) : (
              <div className="program-table-wrap">
                <table className="program-table" data-testid="program-app-table">
                  <thead>
                    <tr><th>ID</th><th>Name</th><th>Domain</th><th>Stage</th><th>7R</th><th /></tr>
                  </thead>
                  <tbody>
                    {programApps.map((row) => (
                      <tr key={row.id}>
                        <td>{row.id}</td>
                        <td>{row.name}</td>
                        <td>{row.domain}</td>
                        <td>{row.stage}</td>
                        <td>{row.recommendation || "—"}</td>
                        <td>
                          <Link className="secondary-button small-btn" to={withApp("/assessment", row.id)} data-testid={`program-open-app-${row.id}`}>
                            Open
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
          <section className="feature-panel analytics-panel" data-testid="program-drill-links">
            <div className="panel-header compact">
              <h2>Program steps — one page each</h2>
            </div>
            <div className="lifecycle-phase-links">
              {PROGRAM_STEPS.map(({ id, label, summary }) => (
                <Link key={id} to={programStepHref(programId, id)} className="lifecycle-phase-link" data-testid={`program-drill-${id}`}>
                  <strong>{label}</strong>
                  <span>{summary}</span>
                  <ChevronRight size={16} />
                </Link>
              ))}
            </div>
          </section>
        </>
      )}

      {view === "step" && module === "govern" && pane === "charter" && (
        <section className="admin-section" data-testid="program-charter-summary">
          <p className="kicker">LEVEL 1 · {core?.record?.id}</p>
          <p className="admin-note">{core?.record?.description}</p>
          <p className="muted">Lifecycle {core?.record?.lifecycle} · Sponsor {core?.record?.executive_sponsor} · PM {core?.record?.program_manager}</p>
          <p className="muted small-note" data-testid="program-funding-state">
            Funding: <b>{programRecord.funding_state || "FUNDED"}</b>
            {programRecord.funding_rationale ? ` · ${programRecord.funding_rationale}` : ""}
          </p>
          {programRecord.funding_state === "UNFUNDED" && (
            <p className="muted small-note" data-testid="program-funding-watch">UNFUNDED — project create and wave assign are blocked until CIO or Product Owner sets funding.</p>
          )}
          <div className="metric-grid" data-testid="program-core-counts">
            {[
              ["Assessed", core?.counts?.assessed],
              ["Completed", core?.counts?.completed],
              ["Budget", core?.record?.budget ? `$${(core.record.budget / 1_000_000).toFixed(1)}M` : "—"],
              ["Health", `${core?.health?.score} ${core?.health?.rag}`],
            ].map(([label, value]) => (
              <div key={label} className="metric"><div><small>{label}</small><strong>{value ?? "—"}</strong></div></div>
            ))}
          </div>
          {(core?.risks || []).filter((r) => r.severity === "red").map((r) => (
            <article key={r.id} className="admin-card" data-testid={`program-risk-${r.id}`}>
              <Badge tone="rose">{r.id} · score {r.score}</Badge>
              <strong>{r.description}</strong>
              <p>Owner: {r.owner}{r.owner_state === "UNKNOWN" ? " · UNKNOWN (not guessed)" : ""}</p>
            </article>
          ))}
          <h3 className="program-h3">Charter</h3>
          <article className="admin-card">
            <p>{core?.record?.charter?.business_problem}</p>
            <p>In scope: {core?.record?.charter?.in_scope}</p>
            <p className="muted small-note" data-testid="program-charter-signature">
              {charterSigned
                ? `Signed by ${programRecord.charter?.signature?.signed_by} · ${programRecord.charter?.signature?.signed_role} · ${String(programRecord.charter?.signature?.signed_at || "").slice(0, 10)}`
                : "Charter not signed — human sign-off required before funded execution."}
            </p>
          </article>
          {charterMsg && <p className="admin-note" data-testid="program-charter-msg">{charterMsg}</p>}
          {maySignCharter && !charterSigned && (
            <button
              type="button"
              className="primary-button"
              data-testid="program-sign-charter"
              onClick={async () => {
                try {
                  await signProgramCharter(programId, { note: "Charter approved in product." });
                  setCharterMsg("Charter signed.");
                  await reloadProgram();
                } catch (err) {
                  setCharterMsg(err.response?.data?.detail || "Charter sign failed");
                }
              }}
            >
              Sign charter
            </button>
          )}
          {maySetFunding && (
            <div className="login-actions" style={{ marginTop: 16, maxWidth: 520 }}>
              <label>
                Funding state
                <select data-testid="program-funding-select" value={fundingState} onChange={(e) => setFundingState(e.target.value)}>
                  {["UNFUNDED", "FUNDED", "ON_HOLD"].map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </label>
              <label>
                Rationale
                <input data-testid="program-funding-rationale" value={fundingRationale} onChange={(e) => setFundingRationale(e.target.value)} placeholder="Why funding changed" />
              </label>
              <button
                type="button"
                className="secondary-button"
                data-testid="program-set-funding"
                onClick={async () => {
                  try {
                    const { data: payload } = await updateProgramFunding(programId, {
                      funding_state: fundingState,
                      rationale: fundingRationale,
                    });
                    setFundingMsg(payload.watch_note || `Funding set to ${fundingState}`);
                    await reloadProgram();
                  } catch (err) {
                    setFundingMsg(err.response?.data?.detail || "Funding update failed");
                  }
                }}
              >
                Update funding
              </button>
            </div>
          )}
          {fundingMsg && <p className="admin-note" data-testid="program-funding-msg">{fundingMsg}</p>}
          <h3 className="program-h3">Funded roadmap</h3>
          <article className="admin-card" data-testid="program-funded-roadmap">
            <p>
              {fundedRoadmap.fiscal_year || "FY—"} · envelope ${((fundedRoadmap.envelope_budget || 0) / 1_000_000).toFixed(1)}M ·{" "}
              <b>{fundedRoadmap.state || "PENDING"}</b>
            </p>
            <p className="muted small-note">
              {(fundedRoadmap.application_ids || []).length} application(s) approved for execution.
              {fundedRoadmap.state === "APPROVED" && fundedRoadmap.approved_by
                ? ` · Approved by ${fundedRoadmap.approved_by} (${fundedRoadmap.approved_role})`
                : ""}
            </p>
            {fundedRoadmap.rationale && <p className="muted small-note">{fundedRoadmap.rationale}</p>}
          </article>
          {roadmapMsg && <p className="admin-note" data-testid="program-roadmap-msg">{roadmapMsg}</p>}
          {mayApproveRoadmap && fundedRoadmap.state !== "APPROVED" && (
            <button
              type="button"
              className="primary-button"
              data-testid="program-approve-roadmap"
              onClick={async () => {
                try {
                  await approveFundedRoadmap(programId, { note: "CIO approved funded set in product." });
                  setRoadmapMsg("Funded roadmap approved.");
                  await reloadProgram();
                } catch (err) {
                  setRoadmapMsg(err.response?.data?.detail || "Roadmap approval failed");
                }
              }}
            >
              Approve funded roadmap
            </button>
          )}
          {mayWrite && (
            <button
              type="button"
              className="secondary-button"
              data-testid="program-draft-roadmap"
              style={{ marginTop: 8 }}
              onClick={async () => {
                try {
                  const ids = programApps.map((a) => a.id);
                  await draftFundedRoadmap(programId, {
                    fiscal_year: fundedRoadmap.fiscal_year || "FY26",
                    envelope_budget: fundedRoadmap.envelope_budget || 5000000,
                    application_ids: ids,
                    wave_ids: fundedRoadmap.wave_ids || ["WAVE-01", "WAVE-02", "WAVE-03"],
                    rationale: "Refresh roadmap from current programme applications.",
                  });
                  setRoadmapMsg("Roadmap draft saved (PENDING approval).");
                  await reloadProgram();
                } catch (err) {
                  setRoadmapMsg(err.response?.data?.detail || "Roadmap draft failed");
                }
              }}
            >
              Save roadmap draft from apps
            </button>
          )}
        </section>
      )}

      {view === "step" && module === "projects" && (
        <section className="admin-section" data-testid="program-projects">
          <p className="kicker">LEVEL 2 · MODERNIZATION PROJECTS</p>
          <p className="admin-note">{core?.rule}</p>
          {projectMsg && <p className="admin-note" data-testid="program-project-msg">{projectMsg}</p>}
          {mayCreateApp && (
            <div className="intro-actions" style={{ marginBottom: 16 }}>
              <button type="button" className="primary-button" data-testid="program-add-application-projects" onClick={() => setCreateAppOpen(true)}>
                <Plus size={16} /> Add application to program
              </button>
            </div>
          )}
          <div className="program-table-wrap">
            <table className="program-table" data-testid="program-project-table">
              <thead>
                <tr><th>Project</th><th>Apps</th><th>7R</th><th>Status</th><th>Estimate</th><th>Grounding</th><th>RAG</th></tr>
              </thead>
              <tbody>
                {(core?.projects || []).map((row) => (
                  <tr key={row.id}>
                    <td>{row.id} · {row.name}</td>
                    <td>{(row.application_ids || []).join(", ")}</td>
                    <td>{row.modernization_decision}</td>
                    <td>{row.status}</td>
                    <td>{row.estimate_hours ?? "—"} h</td>
                    <td>{row.estimate_grounding}</td>
                    <td>{row.schedule_rag} / {row.risk_rag}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <h3 className="program-h3">WBS skeleton · PRJ-018</h3>
          <div className="program-table-wrap">
            <table className="program-table">
              <thead><tr><th>WBS</th><th>Work</th><th>Hours</th><th>% Complete</th><th>Status</th></tr></thead>
              <tbody>
                {(core?.wbs || []).map((row) => (
                  <tr key={row.id}>
                    <td>{row.id}</td>
                    <td>{row.description}</td>
                    <td>{row.estimated_effort_hours}</td>
                    <td>{row.percent_complete}</td>
                    <td>{row.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <h3 className="program-h3">Create project (gated)</h3>
          <p className="muted">An application is not a project. Retain is rejected. Draft MDR is rejected. Authorized execution strategies may create one workstream.</p>
          {mayWrite && (
          <div className="login-actions" style={{ maxWidth: 720 }}>
            {["APP-055", "APP-207", "APP-104"].map((id) => (
              <button key={id} type="button" className="secondary-button" data-testid={`program-create-project-${id}`} onClick={() => runCreateProject(id)}>
                Create project · {id}
              </button>
            ))}
          </div>
          )}
        </section>
      )}

      {view === "step" && module === "govern" && pane === "planning" && (
        <section className="admin-section" data-testid="program-planning">
          <div className="admin-tree">
            {planning?.questions?.map((row) => (
              <article key={row.question} className="admin-card">
                <small>{row.maps_to}</small>
                <strong>{row.question}</strong>
                <p>{row.answer}</p>
              </article>
            ))}
          </div>
          <article className="admin-card" style={{ marginTop: 16 }}>
            <small>DERIVED OUTPUT · NOT AN LLM</small>
            <strong>{planning?.ai_output?.executive_summary}</strong>
            <p>Objective: {planning?.ai_output?.migration_objective}</p>
            <p>Timeline: {planning?.ai_output?.estimated_timeline} · programme risk {planning?.ai_output?.risk_score}</p>
            <p>7R mix: {(planning?.ai_output?.recommended_7r || []).map(([k, v]) => `${k} (${v})`).join(" · ")}</p>
          </article>
          <h3 className="program-h3">Mandatory capabilities</h3>
          <div className="program-checks">{(data.mandatory || []).map((item) => <span key={item} className="ok">{item} ✔</span>)}</div>
          <h3 className="program-h3">Decision pipeline</h3>
          <div className="admin-tree">
            {(inputs?.pipeline || []).map((step) => (
              <article key={step.id} className="admin-card"><small>{step.id}</small><strong>{step.label}</strong><p>{step.detail}</p></article>
            ))}
          </div>
        </section>
      )}

      {view === "step" && module === "govern" && pane === "inputs" && (
        <section className="admin-section" data-testid="program-inputs">
          <p className="admin-note">{inputs?.principle} Eight frozen application domains stay. These categories are structured fields with provenance — not a paragraph.</p>
          <h3 className="program-h3">Ingest channels</h3>
          <div className="admin-tree">
            {(inputs?.channels || []).map((ch) => (
              <article key={ch.id} className="admin-card">
                <Badge tone={ch.now ? "green" : "slate"}>{ch.now ? "NOW" : "NEXT"}</Badge>
                <strong>{ch.label}</strong>
                <p>{ch.note}</p>
              </article>
            ))}
          </div>
          {(inputs?.categories || []).map((cat) => (
            <div key={cat.id} style={{ marginTop: 18 }}>
              <h3 className="program-h3">{cat.n}. {cat.title} · domain {cat.domain} · {cat.coverage}</h3>
              <div className="program-table-wrap">
                <table className="program-table">
                  <thead><tr><th>Field</th><th>Value</th><th>State</th><th>Source</th></tr></thead>
                  <tbody>
                    {(cat.fields || []).map((f) => (
                      <tr key={f.id}>
                        <td>{f.id}</td>
                        <td>{f.value}</td>
                        <td>{f.state}</td>
                        <td>{f.source}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </section>
      )}

      {view === "step" && module === "execution" && pane === "dependencies" && (
        <section className="admin-section" data-testid="program-dependencies">
          <p className="muted">{dependencies?.note}</p>
          <h3 className="program-h3">First-class Dependency entities</h3>
          <div className="program-table-wrap">
            <table className="program-table" data-testid="program-dep-entities">
              <thead>
                <tr>
                  <th>ID</th><th>Source</th><th>Target</th><th>Type</th><th>Criticality</th>
                  <th>Owner</th><th>Blocking</th><th>Source wave</th><th>Target wave</th>
                </tr>
              </thead>
              <tbody>
                {(dependencies?.entities || []).map((row) => (
                  <tr key={row.id}>
                    <td>{row.id}</td>
                    <td>{row.source_name}</td>
                    <td>{row.target_name}</td>
                    <td>{row.dep_type}</td>
                    <td>{row.criticality}</td>
                    <td>{row.owner}</td>
                    <td>{row.blocking ? "Yes" : "No"}</td>
                    <td>{row.proposed_source_wave || row.source_wave || "—"}</td>
                    <td>{row.target_wave || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {mayWrite && (
            <>
          <h3 className="program-h3">CSV ingest (Now) · CMDB/GitHub Next</h3>
          <textarea className="program-csv" value={csv} onChange={(e) => setCsv(e.target.value)} rows={4} />
          <button type="button" className="secondary-button" onClick={runIngest}>Ingest CSV</button>
          {ingestMsg && <p className="muted">{ingestMsg}</p>}
            </>
          )}
          <h3 className="program-h3">Application graph overlay</h3>
          <div className="admin-tree" style={{ marginBottom: 16 }}>
            {(dependencies?.flags || []).map((flag) => (
              <article key={flag.code + (flag.application || "")} className="admin-card">
                <Badge tone={flag.severity === "high" ? "rose" : "amber"}>{flag.severity}</Badge>
                <strong>{flag.title}</strong>
                <p>{flag.detail}</p>
              </article>
            ))}
          </div>
          <div className="program-table-wrap">
            <table className="program-table" data-testid="program-dep-table">
              <thead><tr><th>Dependency</th><th>Type</th><th>Criticality</th><th>Owner</th><th>Application</th></tr></thead>
              <tbody>
                {(dependencies?.rows || []).map((row) => (
                  <tr key={row.id}>
                    <td>{row.from_label} → {row.to_label}</td>
                    <td>{row.type}</td>
                    <td>{row.criticality}</td>
                    <td>{row.owner}</td>
                    <td>{row.application ? <Link to={`/?app=${row.application}`}>{row.application}</Link> : row.application_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {view === "step" && module === "execution" && pane === "waves" && (
        <section className="admin-section" data-testid="program-waves">
          <p className="admin-note">{waves?.rule}</p>
          {mayWrite && (
          <button type="button" className="primary-button" data-testid="program-wave-check" onClick={runCheck}>
            Check: Payment API in Wave 1
          </button>
          )}
          {check && (
            <div className={check.ok ? "admin-note" : "error-message"} data-testid="program-wave-check-result">
              <p>{check.detail}</p>
              {(check.alerts_sent || []).map((sent) => (
                <div key={sent.event_id} className="alert-mail-list">
                  <p>Emailed {sent.emails_sent || sent.recipient_count} function owners ({sent.channel}).</p>
                  <ul>
                    {(sent.recipients || []).map((rec) => (
                      <li key={rec.id}>{rec.owner} · {rec.role} · {rec.email} · {rec.status}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
          <h3 className="program-h3">Committed (stored on the application)</h3>
          <div className="admin-tree">
            {(waves?.committed || []).map((wave) => (
              <article key={wave.id} className="admin-card">
                <small>{wave.id}</small>
                <strong>{wave.name}</strong>
                <p>{wave.intent}</p>
                <p>{wave.count} app(s) · {wave.status}</p>
                <ul>{(wave.applications || []).map((app) => <li key={app.id}><Link to={`/governance?app=${app.id}`}>{app.id} {app.name}</Link></li>)}</ul>
              </article>
            ))}
          </div>
          <h3 className="program-h3">Recommended (derived · not assigned)</h3>
          <div className="admin-tree">
            {(waves?.recommended || []).map((wave) => (
              <article key={`r-${wave.id}`} className="admin-card" data-testid={`wave-rec-${wave.id}`}>
                <small>Risk {wave.risk_score} · {wave.id}</small>
                <strong>{wave.name}</strong>
                <p>Why this wave: {wave.why_this_wave}</p>
                <p>{wave.estimated_duration}</p>
                <ul>{(wave.applications || []).map((app) => (
                  <li key={app.id}>{app.id} · rec {app.recommended_wave} vs committed {app.committed_wave || "none"} · risk {app.risk_score}</li>
                ))}</ul>
              </article>
            ))}
          </div>
        </section>
      )}

      {view === "step" && module === "execution" && pane === "milestones" && (
        <section className="admin-section" data-testid="program-milestones">
          {(waves?.committed || []).filter((w) => w.id !== "UNASSIGNED").map((wave) => (
            <div key={wave.id} style={{ marginBottom: 22 }}>
              <h3 className="program-h3">{wave.id} · {wave.name}</h3>
              <div className="admin-tree">
                {(wave.milestones || []).map((ms) => (
                  <article key={ms.id} className="admin-card">
                    <Badge tone={tone(ms.tone)}>{ms.status}</Badge>
                    <strong>{ms.label}</strong>
                    <small>{ms.owner} · {ms.gate}</small>
                  </article>
                ))}
              </div>
            </div>
          ))}
        </section>
      )}

      {view === "step" && module === "govern" && pane === "raid" && (
        <section className="admin-section" data-testid="program-raid">
          {["R", "A", "I", "D"].map((kind) => (
            <div key={kind} style={{ marginBottom: 18 }}>
              <h3 className="program-h3">{kind === "R" ? "Risks" : kind === "A" ? "Assumptions" : kind === "I" ? "Issues" : "Dependencies"}</h3>
              <div className="admin-tree">
                {(raid?.[kind] || []).map((item) => (
                  <article key={item.id} className="admin-card">
                    <Badge tone={tone(item.status)}>{item.status}</Badge>
                    <strong>{item.item}</strong>
                    <p>Owner: {item.owner}{item.owner_state === "UNKNOWN" ? " · UNKNOWN (not guessed)" : ""}</p>
                    <p>{item.mitigation}</p>
                    {item.score != null && <small>Score {item.probability}×{item.impact} = {item.score}</small>}
                    {(item.impact_chain || []).length > 0 && (
                      <ol className="alert-mail-list">{item.impact_chain.map((step) => <li key={step}>{step}</li>)}</ol>
                    )}
                    {item.escalation && <small>Escalate: {item.escalation}</small>}
                  </article>
                ))}
              </div>
            </div>
          ))}
        </section>
      )}

      {view === "step" && module === "govern" && pane === "alerts" && (
        <section className="admin-section" data-testid="program-alerts">
          <p className="admin-note">{alerts?.rule}</p>
          <p className="muted">Channel: {alerts?.channel}. {alerts?.smtp_configured ? "SMTP is configured — mail is handed to the function owner inbox." : "SMTP_HOST is not set — the same email is recorded in the tenant outbox addressed to each function owner."} {alerts?.channel_next}</p>
          {mayWrite && (
          <button type="button" className="primary-button" data-testid="program-alert-dispatch" onClick={runDispatch}>
            Email all function owners
          </button>
          )}
          {dispatchMsg && <p className="admin-note" data-testid="program-alert-dispatch-result">{dispatchMsg}</p>}
          <h3 className="program-h3">Function owners</h3>
          <div className="program-table-wrap">
            <table className="program-table" data-testid="program-alert-owners">
              <thead><tr><th>Function</th><th>Owner</th><th>Email</th><th>Lane</th></tr></thead>
              <tbody>
                {(alerts?.stakeholders || []).map((row) => (
                  <tr key={row.id}>
                    <td>{row.role}</td>
                    <td>{row.owner}</td>
                    <td>{row.email}</td>
                    <td>{row.lane}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <h3 className="program-h3">Pending</h3>
          {(alerts?.pending || []).length === 0 && <p className="muted">No blocking cutover alerts.</p>}
          <div className="admin-tree">
            {(alerts?.pending || []).map((item) => (
              <article key={item.id} className="admin-card">
                <Badge tone="rose">{item.severity}</Badge>
                <strong>{item.title}</strong>
                <p>{item.message}</p>
                <small>{item.mitigation}</small>
              </article>
            ))}
          </div>
          <h3 className="program-h3">Sent to function-owner email</h3>
          {(alerts?.sent || []).length === 0 && <p className="muted">No emails recorded yet.</p>}
          {(alerts?.sent || []).slice(0, 8).map((sent) => (
            <article key={sent.event_id} className="admin-card" style={{ marginBottom: 12 }} data-testid="program-alert-sent">
              <small>{sent.created_at} · {sent.trigger} · {sent.channel} · {sent.risk_id || "unbound"} · {sent.emails_sent || sent.recipient_count} mailed</small>
              <strong>{sent.title}</strong>
              <p>{sent.message}</p>
              <ul className="alert-mail-list">
                {(sent.recipients || []).map((rec) => (
                  <li key={rec.id}>{rec.owner} &lt;{rec.email}&gt; · {rec.status} · {rec.delivery}</li>
                ))}
              </ul>
            </article>
          ))}
        </section>
      )}

      {view === "step" && module === "govern" && pane === "dashboard" && (
        <section className="admin-section" data-testid="program-dashboard">
          <div className="metric-grid">
            {[
              ["Total apps", dashboard?.total_apps],
              ["Completed", dashboard?.completed],
              ["In progress", dashboard?.in_progress],
              ["Blocked", dashboard?.blocked],
              ["High risk", dashboard?.high_risk],
              ["Est. savings", dashboard?.estimated_savings],
            ].map(([label, value]) => (
              <div key={label} className="metric"><div><small>{label}</small><strong>{value}</strong></div></div>
            ))}
          </div>
        </section>
      )}

      {view === "step" && module === "govern" && pane === "approvals" && (
        <section className="admin-section" data-testid="program-approvals">
          <p className="admin-note">{approvals?.rule}</p>
          <div className="admin-tree">
            {(approvals?.chain || []).map((step) => (
              <article key={step.id} className="admin-card">
                <small>{step.gate} · {step.pending_by || step.role}</small>
                <strong>{step.label}</strong>
                <p>{step.open_count} application(s) still open</p>
                <ul>{(step.open || []).slice(0, 6).map((app) => <li key={app.id}>{app.id} · {app.state} · {app.pending_by || step.pending_by}</li>)}</ul>
              </article>
            ))}
          </div>
        </section>
      )}

      {view === "step" && module === "execution" && pane === "readiness" && (
        <section className="admin-section" data-testid="program-readiness">
          {(readiness || []).map((row) => (
            <article key={row.id} className="admin-card" style={{ marginBottom: 12 }}>
              <strong>{row.name}</strong>
              <small>{row.id}</small>
              <div className="program-checks">
                {(row.checks || []).map((chk) => (
                  <span key={chk.id} className={chk.ok ? "ok" : "no"}>{chk.label} {chk.ok ? "✔" : "⏳"}</span>
                ))}
              </div>
            </article>
          ))}
        </section>
      )}

      {view === "step" && module === "execution" && pane === "hypercare" && (
        <section className="admin-section" data-testid="program-hypercare">
          {(hypercare || []).length === 0 && <p className="muted">No deployment/hypercare records yet.</p>}
          <div className="admin-tree">
            {(hypercare || []).map((row) => (
              <article key={row.id} className="admin-card">
                <strong>{row.name}</strong>
                <small>{row.state} · incidents {row.incidents}</small>
                <p>{row.health}</p>
                <p>MTTR: {row.mttr}</p>
                <p>Lessons: {row.lessons}</p>
                <p>{row.next_wave}</p>
              </article>
            ))}
          </div>
        </section>
      )}

      {view === "step" && module === "govern" && pane === "copilot" && (
        <section className="admin-section" data-testid="program-copilot">
          <p className="kicker">PROGRAM COPILOT · DERIVED, NOT LIVE</p>
          <p className="admin-note">{copilot?.rule}</p>
          <div className="login-actions" style={{ maxWidth: 520 }}>
            {(copilot?.prompts || []).map((p) => (
              <button key={p.id} type="button" className="secondary-button" disabled={asking} onClick={() => runAsk(p.question)}>{p.label}</button>
            ))}
          </div>
          {(ask || (copilot?.samples || [])[0]) && (
            <article className="admin-card" style={{ marginTop: 18 }} data-testid="program-copilot-answer">
              <small>MODEL={String((ask || copilot.samples[0]).model)} · {(ask || copilot.samples[0]).kind}</small>
              <strong>{(ask || copilot.samples[0]).answer}</strong>
              <ul>{((ask || copilot.samples[0]).citations || []).map((c) => <li key={c}>{c}</li>)}</ul>
            </article>
          )}
        </section>
      )}

      {view === "step" && module === "govern" && pane === "dashboard" && (
        <section className="admin-section" data-testid="program-data-model">
        <p className="kicker">ENTERPRISE DATA MODEL · THIS POC</p>
        <div className="admin-tree">
          {(data.data_model || []).map((row) => (
            <article key={row.entity} className="admin-card">
              <strong>{row.entity}</strong>
              <p>{row.now}</p>
              <small>{row.collection}</small>
            </article>
          ))}
        </div>
      </section>
      )}

      {view === "step" && (
        <SubFlowPager items={PROGRAM_FLOWS} focus={module} testId="program-pager-bottom" buildHref={stepHref} />
      )}

      {createAppOpen && programId && (
        <CreateAppModal
          programId={programId}
          programName={core?.record?.name || programMeta?.name}
          onClose={() => setCreateAppOpen(false)}
          onCreate={async (body) => {
            const { data: payload } = await createApplication(body);
            const created = payload?.application || payload;
            setCreateAppOpen(false);
            setCreateAppMsg(`Created ${created.id} in ${programId}`);
            await reloadProgram();
            await refreshPrograms();
          }}
        />
      )}
    </div>
  );
}